/**
 * 通用分页表格。
 *
 * 15 个列表页共用一套「筛选 → 请求 → 表格 → 分页」逻辑，页面只声明列与筛选项。
 * 这不是「泛型 API」——后端仍是每个资源一个显式 DTO 接口，
 * 通用的只有前端的渲染骨架。
 *
 * 筛选条件进 URL query，便于把某个受权限保护的排障视图直接发给同事
 * （apps/admin/README.md「技术边界」）。
 */

import { useMemo } from "react";
import { Alert, Card, Space, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, type Paged, type QueryParams } from "../services/api";

export interface DataTableProps<T> {
  /** 后端路径，如 `/api/v1/admin/users`。 */
  path: string;
  columns: ColumnsType<T>;
  /** 不进 URL 的固定筛选，例如详情页里锁定的 relationshipId。 */
  fixedParams?: QueryParams;
  /** 允许出现在 URL 里的筛选键。 */
  filterKeys?: string[];
  /**
   * 开启列头排序。排序发生在**服务端**，因为分页的前提是排序已经定好——
   * 在前端排只能排当前这一页。
   *
   * 列上写 `sorter: true` 即可参与，列的 `key` 必须等于后端接受的排序值。
   */
  serverSort?: { default: string; defaultOrder?: "asc" | "desc" };
  /** 多个分页表格共用一个页面 URL 时，可给排序状态使用独立的 query key。 */
  sortQueryKeys?: { sort: string; order: string };
  /** 筛选区域。 */
  toolbar?: React.ReactNode;
  defaultPageSize?: number;
  rowKey?: keyof T & string;
  expandable?: React.ComponentProps<typeof Table<T>>["expandable"];
  onRow?: React.ComponentProps<typeof Table<T>>["onRow"];
  title?: React.ReactNode;
  extra?: React.ReactNode;
  /**
   * 固定表格布局。开了之后不设 width 的列会吃掉剩余空间，
   * 配合单元格里的 `wordBreak` 才能让长文本换行而不是把表格撑宽。
   */
  tableLayout?: "auto" | "fixed";
  /** 关掉横向滚动。长文本列换行时不需要它。 */
  disableHorizontalScroll?: boolean;
}

export function useTableParams(filterKeys: string[] = []) {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const out: QueryParams = {};
    for (const key of filterKeys) {
      const v = searchParams.get(key);
      if (v !== null && v !== "") out[key] = v;
    }
    return out;
  }, [searchParams, filterKeys]);

  /** 一次改多个键。改筛选条件时自动回到第一页，否则会停在一个空页上。 */
  const setParams = (patch: Record<string, string | number | undefined | null>) => {
    const next = new URLSearchParams(searchParams);
    let touchedFilter = false;
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === null || value === "") next.delete(key);
      else next.set(key, String(value));
      if (key !== "page" && key !== "pageSize") touchedFilter = true;
    }
    if (touchedFilter) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const setParam = (key: string, value: string | number | undefined | null) =>
    setParams({ [key]: value });

  return { searchParams, params, setParam, setParams };
}

export function DataTable<T extends object>({
  path,
  columns,
  fixedParams,
  filterKeys = [],
  serverSort,
  sortQueryKeys = { sort: "sort", order: "order" },
  toolbar,
  defaultPageSize = 20,
  rowKey = "id" as keyof T & string,
  expandable,
  onRow,
  title,
  extra,
  tableLayout,
  disableHorizontalScroll,
}: DataTableProps<T>) {
  // serverSort 开启时 sort/order 也是 URL 状态，一并纳入筛选键。
  const allFilterKeys = useMemo(
    () => (serverSort
      ? [...new Set([...filterKeys, sortQueryKeys.sort, sortQueryKeys.order])]
      : filterKeys),
    [filterKeys, serverSort, sortQueryKeys.order, sortQueryKeys.sort],
  );

  const { searchParams, params: filters, setParams } = useTableParams(allFilterKeys);
  const page = Number(searchParams.get("page") ?? 1);
  const pageSize = Number(searchParams.get("pageSize") ?? defaultPageSize);

  const sortKey = searchParams.get(sortQueryKeys.sort) ?? serverSort?.default;
  const sortOrder = searchParams.get(sortQueryKeys.order) ?? serverSort?.defaultOrder ?? "desc";

  const params: QueryParams = { ...fixedParams, ...filters, page, pageSize };
  if (serverSort) {
    if (sortQueryKeys.sort !== "sort") delete params[sortQueryKeys.sort];
    if (sortQueryKeys.order !== "order") delete params[sortQueryKeys.order];
    params["sort"] = sortKey;
    params["order"] = sortOrder;
  }

  const { data, isFetching, error } = useQuery({
    queryKey: [path, params],
    queryFn: () => api.get<Paged<T>>(path, params),
    // 翻页时保留上一页内容，避免表格闪一下空白。
    placeholderData: keepPreviousData,
  });

  // 把当前排序状态回填到列上，否则表头箭头不会亮。
  const decoratedColumns = useMemo(() => {
    if (!serverSort) return columns;
    const active: "ascend" | "descend" = sortOrder === "asc" ? "ascend" : "descend";
    return columns.map((col) => {
      if (!("sorter" in col) || !col.sorter) return col;
      const key = String(col.key ?? ("dataIndex" in col ? col.dataIndex : ""));
      return { ...col, key, sortOrder: sortKey === key ? active : null };
    }) as ColumnsType<T>;
  }, [columns, serverSort, sortKey, sortOrder]);

  return (
    <Card title={title} extra={extra} styles={{ body: { padding: 12 } }}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {toolbar}
        {error ? (
          <Alert type="error" showIcon message={(error as Error).message} />
        ) : (
          <Table<T>
            size="small"
            rowKey={rowKey}
            loading={isFetching}
            columns={decoratedColumns}
            dataSource={data?.items ?? []}
            expandable={expandable}
            onRow={onRow}
            tableLayout={tableLayout}
            scroll={disableHorizontalScroll ? undefined : { x: "max-content" }}
            // 两态而非三态：后端必须有确定的 ORDER BY，不存在「不排序」这个状态。
            sortDirections={["descend", "ascend"]}
            onChange={(_pagination, _filters, sorter) => {
              if (!serverSort) return;
              const s = Array.isArray(sorter) ? sorter[0] : sorter;
              if (!s) return;
              const key = String(s.columnKey ?? s.field ?? serverSort.default);
              setParams({
                [sortQueryKeys.sort]: s.order ? key : serverSort.default,
                [sortQueryKeys.order]: s.order === "ascend" ? "asc" : "desc",
              });
            }}
            pagination={{
              current: page,
              pageSize,
              total: data?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100, 200],
              showTotal: (t) => `共 ${t.toLocaleString()} 条`,
              onChange: (p, ps) => setParams({ page: p, pageSize: ps }),
            }}
          />
        )}
      </Space>
    </Card>
  );
}
